import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../utils/Api";
import { usePersistentForm } from "../hooks/usePersistentForm";
import ErrorModal from "../components/ErrorModal";
import { useErrorModal } from "../hooks/useErrorModal";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import StepNumber from "../components/StepNumber";
import PageIntro from "../components/PageIntro";
import heroRentals from "../assets/heroes/hero-rentals.webp";
import CtaBand from "../components/CtaBand";
import ctaPackages from "../assets/heroes/hero-packages.webp";
import { PAGES } from "../seoContent";
import { whatsappLink, WHATSAPP_MESSAGES } from "../utils/whatsapp";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

const displayPrice = (n) =>
  Number.isInteger(Number(n))
    ? `£${Number(n).toLocaleString("en-GB")}`
    : money(n);

const ensureRentalText = (value) => {
  if (!value) return "";

  const original = String(value).trim();
  const lower = original.toLowerCase();

  if (lower.includes("studio rental")) {
    return original;
  }

  return `${original} studio rental`;
};

const getTodayUK = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter.format(new Date());
};

const formatDateDDMMYYYY = (value) => {
  if (!value) return "—";

  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
};

const FORM_DEFAULTS = {
  name: "",
  email: "",
  phone: "",
  purpose: "",
  notes: "",
};

export default function StudioRentals() {
  const { apiFetch } = useApi();
  const { errorMessage, isErrorOpen, showError, closeError } = useErrorModal();

  const [rentalPlans, setRentalPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState("");
  const [planId, setPlanId] = useState(null);

  const [date, setDate] = useState("");
  const [time, setTime] = useState("");

  const [blockedTimes, setBlockedTimes] = useState([]);
  const [loadingBlockedTimes, setLoadingBlockedTimes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { form, updateField } = usePersistentForm({
    scope: "public-site",
    formId: "studio-rental-booking-details",
    version: "v1",
    initialValues: FORM_DEFAULTS,
    persistFields: ["name", "email", "phone"],
    ttlMs: 1000 * 60 * 60 * 24 * 45,
    debounceMs: 300,
  });

  const minUkDate = useMemo(() => getTodayUK(), []);

  const timeSlots = useMemo(
    () => [
      "09:00",
      "10:00",
      "11:00",
      "12:00",
      "13:00",
      "14:00",
      "15:00",
      "16:00",
      "17:00",
    ],
    [],
  );

  const loadRentalPlans = useCallback(async () => {
    try {
      setLoadingPlans(true);
      setPlansError("");

      const res = await apiFetch("get_studio_rentals");

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load studio rentals.");
      }

      const mappedPlans = (Array.isArray(res.data) ? res.data : []).map(
        (item) => ({
          id: Number(item.id),
          title: item.title,
          duration: ensureRentalText(item.duration),
          description: item.description,
          price: Number(item.price),
          durationHours: Number(item.duration_hours || 0),
        }),
      );

      setRentalPlans(mappedPlans);
      setPlanId((prev) =>
        prev && mappedPlans.some((plan) => plan.id === Number(prev))
          ? Number(prev)
          : mappedPlans.length
            ? mappedPlans[0].id
            : null,
      );
    } catch (err) {
      setPlansError(err.message || "Unable to load studio rentals.");
      setRentalPlans([]);
      setPlanId(null);
    } finally {
      setLoadingPlans(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    loadRentalPlans();
  }, [loadRentalPlans]);

  const selectedPlan = useMemo(() => {
    return (
      rentalPlans.find((plan) => Number(plan.id) === Number(planId)) || null
    );
  }, [rentalPlans, planId]);

  const isFullDay = useMemo(() => {
    return Number(selectedPlan?.durationHours || 0) === 9;
  }, [selectedPlan]);

  const isClosedDay = useMemo(() => {
    if (!date) return false;
    return new Date(`${date}T12:00:00`).getDay() === 0;
  }, [date]);

  const onChange = (key) => (e) => {
    updateField(key, e.target.value);

    if (isErrorOpen) {
      closeError();
    }
  };

  const loadUnavailableTimes = useCallback(async () => {
    if (!date || !selectedPlan?.id) {
      setBlockedTimes([]);
      return;
    }

    try {
      setLoadingBlockedTimes(true);

      const res = await apiFetch("get_studio_rental_unavailable_times", {
        bookingDate: date,
        rentalPlanId: selectedPlan.id,
      });

      if (!res?.success) {
        throw new Error(res?.message || "Unable to load unavailable times.");
      }

      const blocked = Array.isArray(res.blockedTimes)
        ? res.blockedTimes
        : Array.isArray(res.data?.blockedTimes)
          ? res.data.blockedTimes
          : [];

      setBlockedTimes(blocked);
    } catch (err) {
      setBlockedTimes([]);
      showError(err.message || "Unable to check studio availability.");
    } finally {
      setLoadingBlockedTimes(false);
    }
  }, [apiFetch, date, selectedPlan, showError]);

  useEffect(() => {
    loadUnavailableTimes();
  }, [loadUnavailableTimes]);

  useEffect(() => {
    if (isFullDay) {
      setTime("09:00");
      return;
    }

    if (time && blockedTimes.includes(time)) {
      setTime("");
    }
  }, [isFullDay, blockedTimes, time]);

  const onSelectPlan = (id) => {
    setPlanId(id);
    setTime("");

    if (isErrorOpen) {
      closeError();
    }
  };

  const validateForm = () => {
    if (!selectedPlan) return "Please select a rental duration.";
    if (!date) return "Please choose a date.";
    if (isClosedDay) return "We are closed on Sundays. Please choose another day.";
    if (!time) return "Please choose a start time.";
    if (!form.name.trim()) return "Please enter your full name.";
    if (!form.email.trim()) return "Please enter your email address.";
    if (!form.purpose.trim()) return "Please enter the purpose of the rental.";

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) return "Please enter a valid email address.";

    if (!form.phone.trim()) return "Please enter your phone number.";
    if (blockedTimes.includes(time))
      return "The selected time is no longer available.";

    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const error = validateForm();
    if (error) {
      showError(error);
      return;
    }

    if (submitting || !selectedPlan) return;

    try {
      setSubmitting(true);
      closeError();

      const payload = {
        rentalPlanId: selectedPlan.id,
        bookingDate: date,
        startTime: time,
        fullName: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        purpose: form.purpose.trim(),
        notes: form.notes.trim(),
      };

      const res = await apiFetch(
        "create_studio_rental_checkout_session",
        payload,
      );

      if (!res?.success) {
        throw new Error(res?.message || "Unable to start payment.");
      }

      if (!res?.checkoutUrl) {
        throw new Error("Stripe checkout link was not returned.");
      }

      window.location.href = res.checkoutUrl;
    } catch (err) {
      showError(err.message || "Unable to start payment.");
      setSubmitting(false);
    }
  };

  const stepBadge = (n) => <StepNumber n={n} />;

  const cardClass =
    "relative isolate overflow-hidden rounded-3xl border border-black/10 bg-white p-6 shadow-[0_16px_40px_rgba(0,0,0,0.06)] md:p-8";

  const inputClass =
    "w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-black outline-none transition focus:border-pink-500";

  return (
    <>
      <SEO
        title={PAGES["/rentals"].title}
        description={PAGES["/rentals"].description}
        url={window.location.href}
      />

      <main className="min-h-screen bg-white text-black">
        <style>{`
          input[type="date"]::-webkit-calendar-picker-indicator {
            opacity: 0;
            position: absolute;
            right: 0;
            width: 100%;
            height: 100%;
            cursor: pointer;
          }

          input[type="date"] {
            color-scheme: light;
          }
        `}</style>

        <PageHero title="Rent Our Studio" image={heroRentals} />

        <PageIntro>
          Hire our fully equipped photography studio in Rochdale for your own shoot. Choose how long you need, pick a date and start time, and make payment.
        </PageIntro>

        <section className="bg-[#fff7fb]">
          <div className="mx-auto grid max-w-7xl gap-8 px-6 py-12 sm:px-10 lg:grid-cols-12 lg:px-16 xl:px-10">
            <div className="space-y-8 lg:col-span-7">
              {/* Step 1 */}
              <div className={cardClass}>
                <div className="mb-6 flex items-center gap-4">
                  {stepBadge(1)}
                  <h2 className="text-2xl font-semibold">
                    Select rental duration
                  </h2>
                </div>

                {loadingPlans ? (
                  <div className="rounded-2xl border border-black/10 bg-white p-6 text-black/60">
                    Loading studio rentals...
                  </div>
                ) : plansError ? (
                  <div className="rounded-2xl border border-red-500/20 bg-red-50 p-6 text-red-600">
                    {plansError}
                  </div>
                ) : rentalPlans.length === 0 ? (
                  <div className="rounded-2xl border border-black/10 bg-white p-6 text-black/60">
                    No studio rentals are available right now.
                  </div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    {rentalPlans.map((plan) => {
                      const active = Number(plan.id) === Number(planId);

                      return (
                        <button
                          key={plan.id}
                          type="button"
                          onClick={() => onSelectPlan(plan.id)}
                          className={[
                            "flex h-full flex-col rounded-2xl border p-5 text-left transition-all duration-300",
                            active
                              ? "border-pink-500 bg-gradient-to-b from-pink-50 to-white shadow-[0_0_0_1px_rgba(236,72,153,0.25),0_20px_50px_rgba(236,72,153,0.15)]"
                              : "border-black/10 bg-white hover:-translate-y-1 hover:border-pink-300 hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)]",
                          ].join(" ")}
                        >
                          <h3 className="text-lg font-semibold text-black/80">
                            {plan.title}
                          </h3>
                          <p
                            className={[
                              "mt-1 text-4xl font-bold tracking-tight",
                              active ? "text-pink-600" : "text-black",
                            ].join(" ")}
                          >
                            {displayPrice(plan.price)}
                          </p>

                          <div className="mt-5 space-y-2.5 text-sm text-black/75">
                            <div className="flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full bg-pink-400" />
                              <span>{plan.duration}</span>
                            </div>

                            {plan.description && (
                              <p className="leading-6 text-black/60">
                                {plan.description}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Step 2 */}
              <div className={cardClass}>
                <div className="mb-6 flex items-center gap-4">
                  {stepBadge(2)}
                  <h2 className="text-2xl font-semibold">
                    Choose date and time
                  </h2>
                </div>

                <label className="text-sm font-medium text-black/70">
                  Preferred date
                </label>

                <div className="relative mt-3 min-w-0">
                  <input
                    type="date"
                    min={minUkDate}
                    value={date}
                    onChange={(e) => {
                      setDate(e.target.value);
                      setTime("");

                      if (isErrorOpen) {
                        closeError();
                      }
                    }}
                    className={`${inputClass} min-w-0 appearance-none`}
                  />

                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-black/60"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                </div>

                <p className="mt-2 text-xs text-black/50">
                  The studio is open Monday to Saturday, 9am to 6pm, and closed
                  on Sundays.
                </p>

                <label className="mt-6 block text-sm font-medium text-black/70">
                  Start time
                </label>

                {!date ? (
                  <p className="mt-3 text-sm text-black/50">
                    Select a date first.
                  </p>
                ) : isClosedDay ? (
                  <p className="mt-3 text-sm text-red-600">
                    We are closed on Sundays. Please choose another day.
                  </p>
                ) : loadingBlockedTimes ? (
                  <p className="mt-3 text-sm text-black/50">
                    Checking availability...
                  </p>
                ) : isFullDay ? (
                  <div className="mt-3 rounded-2xl border border-pink-500/20 bg-pink-50 px-4 py-4 text-sm text-pink-700">
                    Full day bookings run from 09:00 to 18:00.
                  </div>
                ) : (
                  <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-5">
                    {timeSlots.map((slot) => {
                      const active = time === slot;
                      const blocked = blockedTimes.includes(slot);

                      return (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => {
                            if (blocked) return;
                            setTime(slot);

                            if (isErrorOpen) {
                              closeError();
                            }
                          }}
                          disabled={blocked}
                          className={[
                            "rounded-2xl border px-3 py-3 text-sm font-semibold transition",
                            blocked
                              ? "cursor-not-allowed border-black/5 bg-black/[0.03] text-black/25 line-through"
                              : active
                                ? "border-pink-500 bg-pink-500 text-white shadow-md"
                                : "border-black/10 bg-white text-black/80 hover:border-pink-300 hover:bg-pink-50",
                          ].join(" ")}
                        >
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Step 3 */}
              <form
                id="studio-rental-form"
                noValidate
                onSubmit={onSubmit}
                className={cardClass}
              >
                <div className="mb-6 flex items-center gap-4">
                  {stepBadge(3)}
                  <h2 className="text-2xl font-semibold">Your details</h2>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5 sm:col-span-2">
                    <label className="text-sm font-medium text-black/70">
                      Full name
                    </label>
                    <input
                      className={inputClass}
                      value={form.name}
                      onChange={onChange("name")}
                      required
                      autoComplete="name"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-black/70">
                      Email address
                    </label>
                    <input
                      type="email"
                      className={inputClass}
                      value={form.email}
                      onChange={onChange("email")}
                      required
                      autoComplete="email"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-black/70">
                      Phone number
                    </label>
                    <input
                      className={inputClass}
                      value={form.phone}
                      onChange={onChange("phone")}
                      required
                      autoComplete="tel"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5 sm:col-span-2">
                    <label className="text-sm font-medium text-black/70">
                      Purpose of rental
                    </label>
                    <input
                      placeholder="e.g. photoshoot, video shoot"
                      className={`${inputClass} placeholder:text-black/35`}
                      value={form.purpose}
                      onChange={onChange("purpose")}
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-1.5 sm:col-span-2">
                    <label className="text-sm font-medium text-black/70">
                      Additional notes
                    </label>
                    <textarea
                      rows={4}
                      className={inputClass}
                      value={form.notes}
                      onChange={onChange("notes")}
                    />
                  </div>
                </div>
              </form>
            </div>

            {/* Summary */}
            <aside className="lg:col-span-5">
              <div className="rounded-3xl border border-pink-500/20 bg-[#1c1c1c] p-7 text-white shadow-[0_20px_60px_rgba(0,0,0,0.25)] lg:sticky lg:top-28">
                <p className="text-sm uppercase tracking-[0.25em] text-pink-400/80">
                  Summary
                </p>
                <h3 className="mt-2 text-2xl font-semibold">Rental overview</h3>

                {!selectedPlan ? (
                  <div className="mt-8 text-sm text-white/70">
                    No rental plan selected.
                  </div>
                ) : (
                  <div className="mt-8 space-y-4 text-sm text-white/75">
                    <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                      <span>Rental plan</span>
                      <span className="text-right font-medium text-white">
                        {selectedPlan.duration}
                      </span>
                    </div>

                    <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                      <span>Date</span>
                      <span className="text-right font-medium text-white">
                        {formatDateDDMMYYYY(date)}
                      </span>
                    </div>

                    <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                      <span>Start time</span>
                      <span className="text-right font-medium text-white">
                        {time || "—"}
                      </span>
                    </div>

                    <div className="mt-6 flex items-center justify-between rounded-2xl bg-white/5 px-4 py-4">
                      <span className="text-base font-semibold text-white">
                        Total
                      </span>
                      <span className="text-2xl font-semibold text-pink-400">
                        {money(selectedPlan.price)}
                      </span>
                    </div>

                    <button
                      type="submit"
                      form="studio-rental-form"
                      disabled={
                        submitting || loadingPlans || loadingBlockedTimes
                      }
                      className={[
                        "w-full rounded-2xl px-6 py-4 text-base font-semibold transition",
                        submitting || loadingPlans || loadingBlockedTimes
                          ? "cursor-not-allowed bg-pink-500/40 text-white/70"
                          : "bg-pink-500 text-white hover:bg-pink-400",
                      ].join(" ")}
                    >
                      {submitting ? "Redirecting..." : "Continue to Payment"}
                    </button>

                    <p className="text-center text-xs leading-relaxed text-white/50">
                      You will be taken to a secure payment page to complete
                      your booking.
                    </p>
                  </div>
                )}

                <div className="mt-6 border-t border-white/10 pt-5 text-sm text-white/70">
                  <p className="font-medium text-white">
                    Questions about the studio?
                  </p>
                  <p className="mt-2">
                    Call{" "}
                    <a
                      href="tel:+447486160965"
                      className="text-pink-400 hover:text-pink-300"
                    >
                      +44 7486 160965
                    </a>{" "}
                    or{" "}
                    <a
                      href={whatsappLink(WHATSAPP_MESSAGES.rental)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-pink-400 hover:text-pink-300"
                    >
                      message us on WhatsApp
                    </a>
                    .
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-6 py-14 sm:px-10 lg:px-16 xl:px-10">
            <CtaBand
              heading="Event Photography & Videography in Rochdale"
              text="Photo and video coverage for weddings, birthdays, corporate events and celebrations across Greater Manchester."
              to="/packages"
              label="Book Event Coverage"
              image={ctaPackages}
            />
          </div>
        </section>
      </main>

      <ErrorModal
        open={isErrorOpen}
        message={errorMessage}
        onClose={closeError}
        autoCloseMs={4000}
      />
    </>
  );
}
